import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {MonthlyReportForClientDgSearchResults} from "../models/datagrid-search-results/monthly-report-for-client.dg-search-results.model";
import {MonthlyReportForClient} from "../models/monthly-report-for-client.model";

/**
 * Serwis warstwy danych obsługujący cykl życia miesięcznych raportów rozliczeniowych
 * dla klientów. Zarządza procesem generowania zestawień,
 * podsumowaniem zamówień oraz dystrybucją faktur i raportów CSV.
 */
@Injectable({
  providedIn: 'root'
})
export class MonthlyReportForClientService implements DataGridServiceInterface<Observable<MonthlyReportForClientDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę wygenerowanych miesięcznych raportów rozliczeniowych
   * w celu zasilenia głównego widoku tabelarycznego (DataGrid).
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<MonthlyReportForClientDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/monthly-reports-client/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new MonthlyReportForClientDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new MonthlyReportForClient(), [],
          vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje konkretne raporty miesięczne na podstawie przekazanych filtrów
   * (np. numer raportu, identyfikator klienta, status opłacenia).
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>,
                   sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<MonthlyReportForClientDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/monthly-reports-client/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new MonthlyReportForClientDgSearchResults(vetineoApiResponse.response.total,
          this.commonUtils.bestTypedArrayCopyEver(new MonthlyReportForClient(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Inicjuje proces generowania / przeliczania miesięcznego raportu finansowego
   * dla wskazanego klienta za konkretny miesiąc rozliczeniowy.
   *
   * @param uuid Identyfikator klienta.
   * @param month Miesiąc rozliczeniowy.
   * @param year Rok rozliczeniowy.
   * @returns Observable emitujący wygenerowany obiekt raportu z zaktualizowanymi kwotami.
   */
  prepareReport(uuid: string, month: string, year: string): Observable<MonthlyReportForClient> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/monthly-reports-client/${year}/${month}/process/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new MonthlyReportForClient(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Zleca systemowi dystrybucję wygenerowanej faktury
   * powiązanej z konkretnym raportem rozliczeniowym.
   *
   * @param uuid Unikalny identyfikator raportu miesięcznego.
   * @returns Observable potwierdzający zaktualizowany status raportu (np. oznaczony jako 'Wysłany').
   */
  sendInvoice(uuid: string): Observable<MonthlyReportForClient> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/monthly-reports-client/new/invoice/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new MonthlyReportForClient(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zleca asynchroniczne wygenerowanie i ewentualną wysyłkę raportu CSV
   * zawierającego wykaz wszystkich Aktywnych Użytkowników (Active Users)
   * przypisanych do danego raportu.
   *
   * @param uuid Identyfikator raportu.
   * @returns Observable potwierdzający rozpoczęcie przetwarzania żądania na backendzie.
   */
  sendActiveUsersCSVReportRequest(uuid: any) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/monthly-reports-client/au/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Zleca asynchroniczne wygenerowanie raportu CSV zestawiającego wszystkie zamówienia
   * zrealizowane w danym cyklu rozliczeniowym.
   *
   * @param uuid Identyfikator raportu.
   * @returns Observable potwierdzający przyjęcie żądania wygenerowania pliku CSV.
   */
  sendOrdersCSVReportRequest(uuid: any) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/monthly-reports-client/orders/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Pobiera podsumowanie zamówień dla wskazanego raportu.
   *
   * @param uuid Identyfikator raportu miesięcznego.
   * @returns Observable emitujący obiekt podsumowania finansowego.
   */
  getOrderSummaryForMonthlyReport(uuid: any) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/monthly-reports-client/orders-summary/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.summary;
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }

}
